import React, { useState, useEffect, useRef } from 'react';
import api from '../utils/api';
import { useAuth } from '../hooks/useAuth';
import { Upload, Trash2, FileText, Image, File, Paperclip, Download, Eye } from 'lucide-react';
import { DocumentViewer } from './DocumentViewer';

function FileIcon({ mime, nombre }) {
  const ext = (nombre || '').split('.').pop().toLowerCase();
  if ((mime||'').includes('pdf') || ext==='pdf') return <FileText size={15} color="#ef4444"/>;
  if ((mime||'').includes('image') || ['jpg','jpeg','png','gif','bmp'].includes(ext)) return <Image size={15} color="#3b82f6"/>;
  if (['doc','docx'].includes(ext)) return <FileText size={15} color="#2563eb"/>;
  if (['xls','xlsx','csv'].includes(ext)) return <FileText size={15} color="#16a34a"/>;
  return <File size={15} color="#6366f1"/>;
}

function formatSize(bytes) {
  if (!bytes) return '';
  if (bytes < 1024*1024) return `${(bytes/1024).toFixed(0)} KB`;
  return `${(bytes/1024/1024).toFixed(1)} MB`;
}

export default function FileAttachments({ seccion, entidadTipo, entidadId, compact=false, label='Adjuntar archivo' }) {
  const { isAdmin } = useAuth();
  const [archivos, setArchivos] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [visor, setVisor] = useState(null);
  const fileRef = useRef();

  const loadArchivos = () => {
    if (!entidadId) return;
    setLoading(true);
    api.get('/archivos', { params: { entidad_tipo: entidadTipo, entidad_id: entidadId } })
      .then(r => setArchivos(r.data))
      .finally(() => setLoading(false));
  };

  useEffect(() => { loadArchivos(); }, [entidadId]);

  const handleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const formData = new FormData();
    formData.append('archivo', file);
    formData.append('seccion', seccion);
    formData.append('entidad_tipo', entidadTipo);
    formData.append('entidad_id', entidadId);
    setUploading(true);
    try {
      await api.post('/archivos', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      loadArchivos();
    } catch (err) {
      alert(err.response?.data?.error || 'Error al subir el archivo');
    } finally { setUploading(false); e.target.value = ''; }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Eliminar este archivo?')) return;
    await api.delete(`/archivos/${id}`);
    loadArchivos();
  };

  // ── Modo compacto ─────────────────────────────────────────────────────
  if (compact) {
    return (
      <>
        <div style={{ display:'flex', alignItems:'center', gap:4 }}>
          {archivos.length > 0 && (
            <span style={{ display:'flex', alignItems:'center', gap:3, fontSize:'0.75rem', color:'var(--color-primary)', fontWeight:700, cursor:'pointer' }}
              onClick={() => archivos[0] && setVisor(archivos[0])}>
              <Paperclip size={11}/>{archivos.length}
            </span>
          )}
          <label title={label} style={{ cursor:uploading?'wait':'pointer', display:'flex', alignItems:'center', padding:'4px 6px', borderRadius:6, background:'var(--color-gray-1)', border:'1px solid var(--color-gray-3)', color:'var(--color-text-3)' }}>
            {uploading ? <div className="spinner" style={{width:13,height:13}}/> : <Upload size={13}/>}
            <input ref={fileRef} type="file" style={{display:'none'}} onChange={handleUpload}
              accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png" disabled={!entidadId || uploading}/>
          </label>
        </div>
        {visor && <DocumentViewer archivo={visor} onClose={() => setVisor(null)}/>}
      </>
    );
  }

  // ── Modo completo ──────────────────────────────────────────────────────
  return (
    <>
      <div style={{ border:'1px solid var(--color-gray-3)', borderRadius:'var(--radius)', overflow:'hidden', marginTop:16 }}>
        {/* Header */}
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'11px 16px', background:'var(--color-gray-1)', borderBottom:archivos.length>0?'1px solid var(--color-gray-3)':'none' }}>
          <div style={{ display:'flex', alignItems:'center', gap:8, fontWeight:600, fontSize:'0.875rem' }}>
            <Paperclip size={15} color="var(--color-primary)"/>
            Documentos adjuntos
            {archivos.length > 0 && (
              <span style={{ background:'var(--color-primary)', color:'white', fontSize:'0.7rem', fontWeight:700, padding:'1px 7px', borderRadius:10 }}>
                {archivos.length}
              </span>
            )}
          </div>
          <label style={{ cursor:uploading?'wait':'pointer' }}>
            <div className="btn btn-outline btn-sm" style={{ pointerEvents:'none' }}>
              {uploading ? <><div className="spinner" style={{width:13,height:13}}/> Subiendo...</> : <><Upload size={13}/> {label}</>}
            </div>
            <input type="file" style={{display:'none'}} onChange={handleUpload}
              accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.gif" disabled={!entidadId || uploading}/>
          </label>
        </div>

        {/* Lista */}
        {loading ? (
          <div style={{padding:14,textAlign:'center'}}><div className="spinner" style={{margin:'0 auto',width:18,height:18}}/></div>
        ) : archivos.length === 0 ? (
          <div style={{padding:'13px 16px',fontSize:'0.82rem',color:'var(--color-text-3)',textAlign:'center'}}>
            Sin archivos · Sube PDFs, Word, Excel o imágenes
          </div>
        ) : (
          archivos.map(a => (
            <div key={a.id} style={{ display:'flex', alignItems:'center', gap:10, padding:'9px 16px', borderBottom:'1px solid var(--color-gray-2)', background:'white' }}>
              <FileIcon mime={a.tipo_mime} nombre={a.nombre_original}/>
              <div style={{flex:1,minWidth:0}}>
                <div style={{fontSize:'0.82rem',fontWeight:600,whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'}}>
                  {a.nombre_original}
                </div>
                <div style={{fontSize:'0.72rem',color:'var(--color-text-3)'}}>
                  {formatSize(a.tamaño)}
                  {a.subido_por_nombre && ` · ${a.subido_por_nombre}`}
                  {a.created_at && ` · ${new Date(a.created_at).toLocaleDateString('es-ES')}`}
                </div>
              </div>
              <div style={{display:'flex',gap:4,flexShrink:0}}>
                {/* 👁 Previsualizar — abre el visor */}
                <button
                  className="btn btn-ghost btn-icon btn-sm"
                  title="Previsualizar"
                  style={{color:'var(--color-primary)'}}
                  onClick={() => setVisor(a)}
                >
                  <Eye size={14}/>
                </button>
                {/* ⬇ Descargar */}
                <a
                  href={`${window.location.port==='3000'?'http://localhost:3001':''}/api/archivos/${a.id}/descargar?t=${localStorage.getItem('token')||''}`}
                  className="btn btn-ghost btn-icon btn-sm"
                  title="Descargar"
                  target="_blank"
                  rel="noreferrer"
                >
                  <Download size={14}/>
                </a>
                {/* 🗑 Eliminar (solo admin) */}
                {isAdmin() && (
                  <button className="btn btn-ghost btn-icon btn-sm" style={{color:'var(--color-danger)'}}
                    onClick={() => handleDelete(a.id)} title="Eliminar">
                    <Trash2 size={14}/>
                  </button>
                )}
              </div>
            </div>
          ))
        )}

        {!entidadId && (
          <div style={{padding:'9px 16px',fontSize:'0.78rem',color:'var(--color-warning)',background:'var(--color-warning-bg)'}}>
            ⚠ Guarda el registro primero para poder adjuntar archivos
          </div>
        )}
      </div>

      {visor && <DocumentViewer archivo={visor} onClose={() => setVisor(null)}/>}
    </>
  );
}
